/**
 * 权限与菜单 —— 09 章 §9.2.4。
 *
 * **菜单树由路由表推导**，不单独维护一份。两处维护的下场是加了页面忘了加菜单，
 * 或者删了页面菜单还在（点了 404）。
 */

import { computed } from 'vue'
import { defineStore } from 'pinia'

import { useAuthStore } from './auth'
// menu.ts 只对本文件做 `import type`（编译期擦除），所以这条不是运行时循环依赖。
import { useMenuStore } from './menu'
import { MENU_GROUPS, routes, type MtpRouteMeta } from '@/router/routes'
import type { RouteRecordRaw } from 'vue-router'

export interface MenuLeaf {
  kind: 'leaf'
  name: string
  title: string
  icon?: string
  perms?: string[]
}

export interface MenuGroup {
  kind: 'group'
  title: string
  icon?: string
  children: MenuLeaf[]
}

export type MenuNode = MenuLeaf | MenuGroup

/** 把路由表压平成「有 name 且 meta.menu 的叶子」。 */
function collectMenuLeaves(records: readonly RouteRecordRaw[]): MenuLeaf[] {
  const out: MenuLeaf[] = []
  const walk = (list: readonly RouteRecordRaw[]): void => {
    for (const r of list) {
      const meta = r.meta as MtpRouteMeta | undefined
      if (meta?.menu && typeof r.name === 'string') {
        out.push({
          kind: 'leaf',
          name: r.name,
          title: meta.title,
          ...(meta.icon !== undefined ? { icon: meta.icon } : {}),
          ...(meta.perms !== undefined ? { perms: meta.perms } : {}),
        })
      }
      if (r.children) walk(r.children)
    }
  }
  walk(records)
  return out
}

/**
 * 叶子 → 分组结构，**严格保持路由表的出现顺序**。
 *
 * 分组挂在它第一个成员出现的位置上；无分组的叶子留在原位。
 * 这样 09 章 §9.2.3 那棵菜单树的顺序就是 `routes.ts` 里的书写顺序，
 * 不需要第二份顺序声明。
 *
 * 不能把所有无分组叶子提到最前：那样「模型调试」会跑到「服务器」后面，而设计里它在
 * 「评测分析」与「部署与服务」之间。错法的本质是用两个维度（是否分组、书写顺序）
 * 排一个一维列表。
 */
function buildTree(leaves: readonly MenuLeaf[], groupOf: ReadonlyMap<string, string>): MenuNode[] {
  const iconOf = new Map(MENU_GROUPS.map((g) => [g.name, g.icon]))

  const tree: MenuNode[] = []
  const groups = new Map<string, MenuGroup>()

  for (const leaf of leaves) {
    const groupName = groupOf.get(leaf.name)
    if (!groupName) {
      tree.push(leaf)
      continue
    }
    let group = groups.get(groupName)
    if (!group) {
      const icon = iconOf.get(groupName)
      group = {
        kind: 'group',
        title: groupName,
        ...(icon ? { icon } : {}),
        children: [],
      }
      groups.set(groupName, group)
      tree.push(group) // ← 组占住它第一个成员的位置
    }
    group.children.push(leaf)
  }

  return tree
}

export const usePermissionStore = defineStore('permission', () => {
  const auth = useAuthStore()

  /**
   * 权限判定。**空的 `perms` 表示「只需登录」**，返回 true。
   *
   * ⚠️ 这只决定"看得见/看不见"，**不是安全边界**。后端每个接口独立校验
   * tenant + role（契约 §19：`repositories/` 层强制带 `tenant_id`，越权一律 404）。
   * 把前端判定当安全边界的下场是：改一行 JS 就能访问别人的数据。
   */
  const has = (perms?: readonly string[]): boolean => {
    if (!perms || perms.length === 0) return true
    return perms.some((p) => auth.perms.has(p))
  }

  const hasAll = (perms?: readonly string[]): boolean => {
    if (!perms || perms.length === 0) return true
    return perms.every((p) => auth.perms.has(p))
  }

  /** name → group 的映射，从路由表算一次。 */
  const groupOf = computed(() => {
    const map = new Map<string, string>()
    const walk = (list: readonly RouteRecordRaw[]): void => {
      for (const r of list) {
        const meta = r.meta as MtpRouteMeta | undefined
        if (meta?.group && typeof r.name === 'string') map.set(r.name, meta.group)
        if (r.children) walk(r.children)
      }
    }
    walk(routes)
    return map
  })

  /**
   * 由路由表推导的菜单树。**父节点子项全空则自身隐藏**——
   * 一个点开来是空的分组比没有这个分组更让人困惑。
   *
   * 菜单进库之后这套只作**降级兜底**：`/menus/me` 拉不到时用它，
   * 免得整个侧栏空掉、让用户以为这个平台什么功能都没有。
   */
  const routeMenuTree = computed<MenuNode[]>(() => {
    const visible = collectMenuLeaves(routes).filter((leaf) => has(leaf.perms))
    return buildTree(visible, groupOf.value).filter(
      (n) => n.kind === 'leaf' || n.children.length > 0,
    )
  })

  /**
   * 侧栏真正渲染的那棵树。
   *
   * 库里拉到了就用库里的 —— 否则管理员在菜单管理里改完、刷新后看到的还是旧菜单，
   * 会以为是自己没保存成功。只有拉不到才退回路由表。
   */
  const menuTree = computed<MenuNode[]>(() => {
    const menu = useMenuStore()
    return menu.loaded ? menu.uiTree : routeMenuTree.value
  })

  /** 菜单是不是降级来的。界面据此提示，不做无声降级。 */
  const menuDegraded = computed(() => useMenuStore().failed)

  return { has, hasAll, menuTree, routeMenuTree, menuDegraded }
})
